<template>
  <div class="app-bg" :class="{ 'light-theme': isLightTheme }"></div>

  <!-- 全局加载页：TIM 初始化阶段 -->
  <div v-if="bootState !== 'done'" class="boot-screen" :class="{ 'light-theme': isLightTheme }">
    <div class="boot-logo">
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <circle cx="24" cy="24" r="22" stroke="rgba(34,211,185,0.4)" stroke-width="2"/>
        <circle cx="24" cy="24" r="22" stroke="#22D3B9" stroke-width="2"
          stroke-dasharray="138" :stroke-dashoffset="arcOffset"
          stroke-linecap="round" transform="rotate(-90 24 24)"
          class="boot-arc"/>
        <text x="24" y="29" text-anchor="middle" fill="#22D3B9" font-size="16" font-weight="bold">IM</text>
      </svg>
    </div>
    <p class="boot-text">{{ bootText }}</p>
    <p v-if="bootError" class="boot-error">{{ bootError }}</p>
    <button v-if="bootError" class="boot-retry" @click="startBoot">重试</button>
  </div>

  <!-- 正常视图 -->
  <div v-if="bootState === 'done'" class="light-theme-wrapper" :class="{ 'light-theme': isLightTheme }">
    <RouterView v-slot="{ Component, route }">
      <Transition name="page" mode="out-in">
        <component :is="Component" :key="route.path" />
      </Transition>
    </RouterView>
  </div>

  <!-- 全局 Toast -->
  <Transition name="toast">
    <div v-if="globalStore.toastMsg" class="global-toast">
      {{ globalStore.toastMsg }}
    </div>
  </Transition>

  <!-- 权限检查弹窗 -->
  <Transition name="perm-modal">
    <div v-if="showPermModal" class="perm-overlay" @click.self="onPermOverlayClick">
      <div class="perm-card">
        <!-- 状态1：初始提示 -->
        <template v-if="permModalState === 'request'">
          <div class="perm-icon">🎙️</div>
          <h3 class="perm-title">{{ t('permission.title') }}</h3>
          <p class="perm-desc">{{ t('permission.desc') }}</p>
          <div class="perm-actions">
            <button class="perm-btn primary" @click="handleOpenPermissions">
              {{ t('permission.openBtn') }}
            </button>
            <button class="perm-btn secondary" @click="handleSkipPermissions">
              {{ t('permission.skipBtn') }}
            </button>
          </div>
        </template>
        <!-- 状态2：浏览器已禁止 -->
        <template v-else-if="permModalState === 'denied'">
          <div class="perm-icon">🔒</div>
          <h3 class="perm-title">{{ t('permission.deniedTitle') }}</h3>
          <p class="perm-desc">{{ t('permission.deniedDesc') }}</p>
          <div class="perm-steps">
            <div class="perm-step-item">{{ t('permission.deniedStep1') }}</div>
            <div class="perm-step-item">{{ t('permission.deniedStep2') }}</div>
            <div class="perm-step-item">{{ t('permission.deniedStep3') }}</div>
          </div>
          <div class="perm-actions">
            <button class="perm-btn primary" @click="handleRetryPermissions">
              {{ t('permission.deniedBtn') }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { RouterView } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useGlobalStore } from '@/stores/global.js'
import { useUserStore } from '@/stores/user.js'
import { initTIM, isTIMReady } from '@/composables/useTIM.js'
import { emitter } from '@/composables/useTIM.js'
import { getUserSig } from '@/api/im.js'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const globalStore = useGlobalStore()
const userStore = useUserStore()

// 入口参数：用于在 startBoot 和 onSdkReady 之间传递，避免 sessionStorage 竞态
let pendingEntry = null

// boot 状态：loading | done | error
const bootState = ref('loading')
const bootError = ref('')
const bootStepIdx = ref(0)
const bootSteps = computed(() => [t('init.loading'), t('init.connecting'), t('init.loginIM')])
const bootText = computed(() => bootSteps.value[bootStepIdx.value] || t('init.loading'))

// 权限弹窗状态
const showPermModal = ref(false)
const permModalState = ref('request') // 'request' | 'denied'
const permChecked = ref(false) // 是否已检查过权限

// 权限检查：进应用 IM 连接成功后检查一次
async function checkPermissions() {
  if (permChecked.value) return
  permChecked.value = true
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    console.warn('[App] 权限检查跳过: 浏览器不支持 getUserMedia')
    return
  }

  // 1. 用 Permissions API 预检
  let audioState = 'unknown'
  let videoState = 'unknown'
  if (navigator.permissions && navigator.permissions.query) {
    try {
      const [audioPerm, videoPerm] = await Promise.all([
        navigator.permissions.query({ name: 'microphone' }),
        navigator.permissions.query({ name: 'camera' })
      ])
      audioState = audioPerm.state
      videoState = videoPerm.state
    } catch (e) {
      console.warn('[App] Permissions API error:', e.name, e.message)
    }
  }

  console.warn('[App] 权限状态:', { audio: audioState, video: videoState })

  // 2. 根据权限状态处理
  if (audioState === 'denied' || videoState === 'denied') {
    console.warn('[App] 权限状态为 denied，弹出权限提示')
    showPermModal.value = true
    permModalState.value = 'request'
    return
  }

  if (audioState === 'prompt' || videoState === 'prompt') {
    console.warn('[App] 权限状态为 prompt，弹出权限提示让用户授权')
    showPermModal.value = true
    permModalState.value = 'request'
    return
  }

  // 3. granted 或 unknown → 直接尝试获取权限
  console.warn('[App] 开始尝试获取权限')
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true })
    stream.getTracks().forEach(t => t.stop())
    console.warn('[App] getUserMedia 成功，权限已获取')
  } catch (e) {
    console.warn('[App] getUserMedia 失败:', e.name, e.message)
    if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
      console.warn('[App] 权限被拒绝，弹出权限提示')
      showPermModal.value = true
      permModalState.value = 'request'
    }
    // NotReadableError / NotFoundError: 设备问题（被占用或不存在），不阻止，用户可在通话时再试
  }
}

// 点击"去开启权限"
async function handleOpenPermissions() {
  console.warn('[App] 点击了去开启权限按钮')
  try {
    console.warn('[App] 尝试调用 getUserMedia 获取权限')
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true })
    stream.getTracks().forEach(t => t.stop())
    console.warn('[App] getUserMedia 成功，权限获取成功')
    // 成功获取权限，关闭弹窗
    showPermModal.value = false
  } catch (e) {
    console.warn('[App] getUserMedia 失败:', e.name, e.message)
    // 任何失败都显示引导步骤
    console.warn('[App] 显示引导步骤')
    permModalState.value = 'denied'
  }
}

// 点击重试
async function handleRetryPermissions() {
  console.warn('[App] 点击了重试按钮')
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true })
    stream.getTracks().forEach(t => t.stop())
    console.warn('[App] 重试成功，权限获取成功')
    showPermModal.value = false
  } catch (e) {
    console.warn('[App] 重试失败:', e.name, e.message)
    // 仍然失败，保持在引导步骤页面
  }
}

// 跳过权限检查
function handleSkipPermissions() {
  console.warn('[App] 用户点击了暂不需要，关闭权限弹窗')
  showPermModal.value = false
}

// 点击遮罩层不关闭
function onPermOverlayClick() {
  // 不响应
}

// 主题切换
const isLightTheme = ref(
  new URLSearchParams(window.location.search).get('theme') !== 'dark'
)
watch(isLightTheme, (light) => {
  document.body.classList.toggle('dark-theme', !light)
}, { immediate: true })

// loader 动画
const arcOffset = computed(() => 138 - (bootStepIdx.value / 2) * 138)

// SDK_READY 监听器
let sdkReadyHandled = false
function onSdkReady() {
  if (sdkReadyHandled) return
  sdkReadyHandled = true
  emitter.off('SDK_READY', onSdkReady)
  bootStepIdx.value = 2
  setTimeout(() => {
    bootState.value = 'done'
    // 使用 pendingEntry（由 startBoot 设置），不再读取 sessionStorage
    const entry = pendingEntry
    pendingEntry = null

    console.log('[App] onSdkReady, entry:', entry)

    // IM 连接成功后检查权限
    checkPermissions()

    if (entry?.type === 'call' || entry?.strRoomId) {
      // 入口2：直接视频聊天
      const callType = entry.call_type || (entry.strRoomId?.includes('voice') ? 'audio' : 'video')
      router.replace({
        name: 'VideoCall',
        query: {
          to: entry.to || '',
          room_id: entry.strRoomId || '',
          call_type: callType,
          call_id: `call_${Date.now()}`,
          action: 'calling'
        }
      })
    } else if (entry?.type === 'chat' || entry?.to) {
      // 入口3：直接聊天（进入或创建 C2C 会话）
      router.replace({
        name: 'Chat',
        params: { conversationID: `C2C${entry.to}` }
      })
    } else {
      // 入口1：进入会话列表
      router.replace({ name: 'ConversationList' })
    }
  }, 400)
}

async function startBoot() {
  bootError.value = ''
  bootState.value = 'loading'
  bootStepIdx.value = 0
  sdkReadyHandled = false

  // 检查 URL 参数 zmToken（外部入口）
  const urlParams = new URLSearchParams(window.location.search)
  const zmToken = urlParams.get('zmToken')
  const entryType = urlParams.get('type')  // 'call' | 'chat' | 'list'
  const strRoomId = urlParams.get('strRoomId')
  const toUser = urlParams.get('to')
  const callType = urlParams.get('call_type')

  // ===== 【最高优先级】协议检查 =====
  // 有 protocol 参数时：先判断 localStorage 是否已同意过相同协议
  // 未同意 → 保存全部参数，跳转协议页；同意后再恢复参数继续处理
  const protocolParam = urlParams.get('protocol')
  if (protocolParam) {
    let agreedProtocol = null
    try { agreedProtocol = localStorage.getItem('protocol') } catch (e) { /* ignore */ }

    if (agreedProtocol !== protocolParam) {
      console.log('[App] 需要同意协议:', protocolParam)
      // 保存完整入口 URL（含所有参数），同意后恢复
      try {
        sessionStorage.setItem('h5im_protocol_entry_url', window.location.href)
      } catch (e) {
        console.warn('[App] 保存协议入口 URL 失败:', e)
      }
      // 先进入 done 态以渲染路由视图，再跳转协议页
      bootState.value = 'done'
      router.replace({ name: 'Agreement', query: { protocol: protocolParam } })
      return
    }
    // 已同意过相同协议 → 继续正常流程
    console.log('[App] 协议已同意，继续处理:', protocolParam)
  }

  // 获取当前路由信息（用于检测直接访问 Chat/VideoCall 路由的情况）
  const currentRoute = router.currentRoute.value
  const isDirectChatAccess = currentRoute.name === 'Chat' && currentRoute.params.conversationID
  const isDirectCallAccess = currentRoute.name === 'VideoCall' && (currentRoute.query.to || currentRoute.query.room_id)

  // 处理 zmToken 入口参数
  if (zmToken) {
    localStorage.setItem('h5im_token', zmToken)
    // 清理 URL 中的敏感参数
    // 注意：iOS Safari 中直接调用 replaceState 可能导致路由状态混乱
    // 使用 setTimeout 延迟执行以避免 iOS Safari 的兼容性问题
    setTimeout(() => {
      try {
        const cleanUrl = new URL(window.location)
        cleanUrl.searchParams.delete('zmToken')
        cleanUrl.searchParams.delete('type')
        cleanUrl.searchParams.delete('strRoomId')
        cleanUrl.searchParams.delete('to')
        cleanUrl.searchParams.delete('call_type')
        window.history.replaceState({}, '', cleanUrl)
      } catch (e) {
        console.warn('[App] replaceState failed:', e)
      }
    }, 0)
  }

  // 解析入口参数并保存到 pendingEntry
  if (zmToken) {
    if (entryType === 'call' || strRoomId) {
      // 入口2：直接视频聊天
      pendingEntry = {
        type: 'call',
        strRoomId: strRoomId || '',
        to: toUser || '',
        call_type: callType || ''
      }
    } else if (entryType === 'chat' || toUser) {
      // 入口3：直接聊天（进入或创建 C2C 会话）
      pendingEntry = {
        type: 'chat',
        to: toUser || ''
      }
    } else {
      // 有 zmToken 但无具体入口参数，检查是否直接访问路由
      if (isDirectChatAccess) {
        pendingEntry = { type: 'chat', to: currentRoute.params.conversationID.replace('C2C', '') }
      } else if (isDirectCallAccess) {
        pendingEntry = {
          type: 'call',
          strRoomId: currentRoute.query.room_id || '',
          to: currentRoute.query.to || '',
          call_type: currentRoute.query.call_type || ''
        }
      } else {
        pendingEntry = { type: 'list' }
      }
    }
  } else if (isDirectChatAccess) {
    // 无 zmToken 但直接访问 Chat 路由
    pendingEntry = { type: 'chat', to: currentRoute.params.conversationID.replace('C2C', '') }
  } else if (isDirectCallAccess) {
    // 无 zmToken 但直接访问 VideoCall 路由
    pendingEntry = {
      type: 'call',
      strRoomId: currentRoute.query.room_id || '',
      to: currentRoute.query.to || '',
      call_type: currentRoute.query.call_type || ''
    }
  }

  // 检查 token
  const token = localStorage.getItem('h5im_token')
  if (!token) {
    bootState.value = 'done'
    return
  }

  // 如果 TIM 已经就绪，直接完成并根据入口参数跳转
  if (isTIMReady()) {
    bootState.value = 'done'
    sdkReadyHandled = true
    if (route.name === 'Init') {
      // 使用 pendingEntry（由 URL 参数解析设置）
      const entry = pendingEntry
      pendingEntry = null

      console.log('[App] fast path, entry:', entry)

      if (entry?.type === 'call' || entry?.strRoomId) {
        // 入口2：直接视频聊天
        const callType = entry.call_type || (entry.strRoomId?.includes('voice') ? 'audio' : 'video')
        router.replace({
          name: 'VideoCall',
          query: {
            to: entry.to || '',
            room_id: entry.strRoomId || '',
            call_type: callType,
            call_id: `call_${Date.now()}`,
            action: 'calling'
          }
        })
      } else if (entry?.type === 'chat' || entry?.to) {
        // 入口3：直接聊天
        router.replace({
          name: 'Chat',
          params: { conversationID: `C2C${entry.to}` }
        })
      } else {
        // 入口1：进入会话列表
        router.replace({ name: 'ConversationList' })
      }
    }
    return
  }

  bootStepIdx.value = 0

  // Step 1: 获取 IM 签名
  let credentials
  try {
    const sigRes = await getUserSig()
    credentials = sigRes.data
  } catch (e) {
    bootError.value = t('init.error.sigFail')
    return
  }

  if (!credentials?.imUserId || !credentials?.userSig) {
    bootError.value = t('init.error.sigFail')
    return
  }

  // 保存凭证
  userStore.setCredentials(credentials)
  bootStepIdx.value = 1

  // Step 2: 注册 SDK_READY 监听（必须在 initTIM 之前）
  emitter.on('SDK_READY', onSdkReady)

  // Step 3: 初始化 TIM 并登录
  try {
    bootStepIdx.value = 1
    await initTIM(credentials)
  } catch (e) {
    emitter.off('SDK_READY', onSdkReady)
    bootError.value = t('init.error.loginFail', { msg: e.message || e })
    return
  }
}

onMounted(() => {
  startBoot()
})

onUnmounted(() => {
  emitter.off('SDK_READY', onSdkReady)
})
</script>

<style>
/* Boot Screen */
.boot-screen {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #0A0F1E;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
}

.boot-screen.light-theme {
  background: #f0f4f8;
}

/* Page Transition */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.page-enter-from {
  opacity: 0;
  transform: translateX(12px);
}
.page-leave-to {
  opacity: 0;
  transform: translateX(-12px);
}

.light-theme .page-enter-active,
.light-theme .page-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.light-theme .page-enter-from {
  opacity: 0;
  transform: translateX(12px);
}
.light-theme .page-leave-to {
  opacity: 0;
  transform: translateX(-12px);
}

/* Boot Logo */
.boot-logo {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(0.95); }
}

.boot-arc {
  transition: stroke-dashoffset 0.5s ease;
}

.boot-text {
  color: rgba(255,255,255,0.5);
  font-size: 13px;
  letter-spacing: 2px;
}

.boot-error {
  color: #f87171;
  font-size: 13px;
  max-width: 280px;
  text-align: center;
}

.boot-retry {
  background: rgba(34,211,185,0.15);
  border: 1px solid rgba(34,211,185,0.4);
  color: #22D3B9;
  padding: 8px 24px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
}

/* Toast */
.global-toast {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 9999;
  pointer-events: none;
  text-align: center;
  max-width: 80vw;
  word-break: break-word;
}

.toast-enter-active, .toast-leave-active {
  transition: opacity 0.25s;
}
.toast-enter-from, .toast-leave-to {
  opacity: 0;
}

/* Light 主题 Toast */
.light-theme .global-toast {
  background: rgba(255, 255, 255, 0.95);
  color: var(--color-text);
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

/* 权限弹窗 */
.perm-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(8px);
}
.perm-card {
  background: rgba(20, 30, 50, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 32px 24px;
  max-width: 340px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}
.perm-icon {
  font-size: 48px;
  margin-bottom: 16px;
}
.perm-title {
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 12px;
}
.perm-desc {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin: 0 0 24px;
}
.perm-steps {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.7;
  margin: 0 0 24px;
  text-align: left;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 14px 16px;
}
.perm-step-item {
  margin-bottom: 6px;
}
.perm-step-item:last-child {
  margin-bottom: 0;
}
.perm-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.perm-btn {
  width: 100%;
  padding: 13px 20px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}
.perm-btn.primary {
  background: linear-gradient(135deg, #22D3B9, #1ba89c);
  color: #fff;
}
.perm-btn.primary:active {
  opacity: 0.85;
  transform: scale(0.98);
}
.perm-btn.secondary {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.15);
}
.perm-btn.secondary:active {
  background: rgba(255, 255, 255, 0.15);
}

/* 弹窗动画 */
.perm-modal-enter-active, .perm-modal-leave-active {
  transition: opacity 0.3s ease;
}
.perm-modal-enter-from, .perm-modal-leave-to {
  opacity: 0;
}
.perm-modal-enter-active .perm-card, .perm-modal-leave-active .perm-card {
  transition: transform 0.3s ease;
}
.perm-modal-enter-from .perm-card, .perm-modal-leave-to .perm-card {
  transform: scale(0.9) translateY(20px);
}
</style>
